import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { motion, useScroll, useTransform, useInView, useMotionValue, useSpring } from 'framer-motion';
import { 
  ArrowUpRight, 
  ArrowRight,
  ArrowDown,
  Shield, 
  Globe2, 
  Truck, 
  BadgeDollarSign, 
  HeadphonesIcon, 
  FileCheck,
  Play,
  ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { CategoryCard, FeaturedProductCard } from '../components/ProductCard';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Magnetic Button Component
const MagneticButton = ({ children, className, ...props }) => {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 300, damping: 20 });
  const springY = useSpring(y, { stiffness: 300, damping: 20 });

  const handleMouseMove = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (rect) {
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      x.set((e.clientX - centerX) * 0.15);
      y.set((e.clientY - centerY) * 0.15);
    }
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={ref}
      style={{ x: springX, y: springY }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
};

// Animated Counter with Spring
const AnimatedCounter = ({ end, suffix = '', duration = 2.5 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    if (isInView) {
      let startTime;
      const animate = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / (duration * 1000), 1);
        const easeOut = 1 - Math.pow(1 - progress, 4);
        setCount(Math.floor(easeOut * end));
        if (progress < 1) requestAnimationFrame(animate);
      };
      requestAnimationFrame(animate);
    }
  }, [isInView, end, duration]);

  return <span ref={ref} className="font-mono tabular-nums">{count}{suffix}</span>;
};

// Text Reveal Animation
const TextReveal = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <span ref={ref} className="inline-block overflow-hidden">
      <motion.span
        className="inline-block"
        initial={{ y: '100%' }}
        animate={isInView ? { y: 0 } : { y: '100%' }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay }}
      >
        {children}
      </motion.span>
    </span>
  );
};

export const Home = () => {
  const { t } = useLanguage();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 100]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, prodRes] = await Promise.all([
          axios.get(`${API}/categories`),
          axios.get(`${API}/products`)
        ]);
        setCategories(catRes.data);
        setProducts(prodRes.data.slice(0, 4));
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const stats = [
    { value: 50, suffix: '+', label: t('stats.countries'), detail: 'Worldwide' },
    { value: 500, suffix: '+', label: t('stats.products'), detail: 'Premium Quality' },
    { value: 1000, suffix: '+', label: t('stats.partners'), detail: 'Active Partners' },
    { value: 15, suffix: '', label: t('stats.experience'), detail: 'Years of Trust' },
  ];

  const whyUsItems = [
    { icon: Shield, title: t('why_us.quality.title'), description: t('why_us.quality.description') },
    { icon: Globe2, title: t('why_us.global.title'), description: t('why_us.global.description') },
    { icon: Truck, title: t('why_us.logistics.title'), description: t('why_us.logistics.description') },
    { icon: BadgeDollarSign, title: t('why_us.competitive.title'), description: t('why_us.competitive.description') },
    { icon: HeadphonesIcon, title: t('why_us.support.title'), description: t('why_us.support.description') },
    { icon: FileCheck, title: t('why_us.docs.title'), description: t('why_us.docs.description') },
  ];

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="home-page">
      {/* Hero Section - Cinematic Full Screen */}
      <section ref={heroRef} className="relative h-screen overflow-hidden">
        {/* Background Image with Parallax */}
        <motion.div 
          className="absolute inset-0"
          style={{ scale: heroScale }}
        >
          <img
            src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?q=80&w=2070"
            alt="Global Trade"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a1628]/70 via-[#0a1628]/50 to-[#0a1628]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a1628]/80 via-transparent to-transparent" />
        </motion.div>

        {/* Grain Overlay */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
        }} />

        {/* Content */}
        <motion.div 
          className="relative h-full flex flex-col justify-center max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16 pt-24 sm:pt-28 pb-48 sm:pb-48 md:pb-44"
          style={{ y: textY, opacity: heroOpacity }}
        >
          {/* Pre-title */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mb-4 sm:mb-8"
          >
            <span className="inline-flex items-center gap-2 sm:gap-3 text-secondary-500">
              <span className="w-8 sm:w-12 h-px bg-secondary-500" />
              <span className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.2em] sm:tracking-[0.4em]">
                {t('hero.tagline')}
              </span>
            </span>
          </motion.div>

          {/* Main Title */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-heading font-bold text-white leading-[0.95] tracking-tight mb-4 sm:mb-8">
            <TextReveal delay={0.4}>{t('hero.title_line1')}</TextReveal>
            <br />
            <TextReveal delay={0.5}>
              <span className="text-secondary-500">{t('hero.title_line2')}</span>
            </TextReveal>
            <br />
            <TextReveal delay={0.6}>{t('hero.title_line3')}</TextReveal>
          </h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="text-sm sm:text-lg md:text-xl text-white/60 max-w-xl mb-6 sm:mb-12 leading-relaxed"
          >
            {t('hero.subtitle')}
          </motion.p>
        </motion.div>

        {/* Bottom Stats Bar */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="absolute bottom-0 left-0 right-0 border-t border-white/10 bg-[#0a1628]/90 backdrop-blur-sm"
        >
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-10 lg:px-16">
            <div className="grid grid-cols-2 md:grid-cols-4">
              {stats.map((stat, index) => (
                <div 
                  key={index} 
                  className={`py-3 sm:py-6 md:py-8 px-3 sm:px-4 md:px-8 text-center md:text-left ${
                    index < 2 ? 'border-b md:border-b-0 border-white/10' : ''
                  } ${index % 2 === 0 ? 'border-r border-white/10' : 'md:border-r md:border-white/10'} ${
                    index === 3 ? 'md:border-r-0' : ''
                  }`}
                  data-testid={`stat-${index}`}
                >
                  <p className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-0.5 sm:mb-1">
                    <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                  </p>
                  <p className="text-[9px] sm:text-xs font-mono uppercase tracking-wider text-white/40">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-32 right-6 md:right-16 hidden md:flex flex-col items-center gap-2"
        >
          <span className="text-xs font-mono uppercase tracking-wider text-white/40 writing-vertical">
            Scroll
          </span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <ArrowDown className="w-4 h-4 text-white/40" />
          </motion.div>
        </motion.div>
      </section>

      {/* Marquee Section */}
      <section className="py-6 bg-secondary-500 overflow-hidden">
        <motion.div 
          className="flex whitespace-nowrap"
          animate={{ x: [0, -1000] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        >
          {[...Array(10)].map((_, i) => (
            <span key={i} className="mx-8 text-sm font-bold uppercase tracking-[0.3em] text-primary-900">
              {t('marquee.quality')} <span className="mx-4">◆</span> {t('marquee.network')} <span className="mx-4">◆</span> {t('marquee.partners')} <span className="mx-4">◆</span> {t('marquee.delivery')} <span className="mx-4">◆</span>
            </span>
          ))}
        </motion.div>
      </section>

      {/* About Section - Split */}
      <section className="py-24 md:py-32 bg-[#f9f9f7]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-flex items-center gap-3 text-secondary-600 mb-6">
                <span className="w-12 h-px bg-secondary-500" />
                <span className="text-xs font-mono uppercase tracking-[0.3em]">{t('about_section.tagline')}</span>
              </span>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-primary-900 leading-[1.1] mb-8">
                {t('about_section.title')}
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                {t('about_section.description')}
              </p>
              <div className="grid grid-cols-2 gap-8 mb-10">
                <div>
                  <p className="text-4xl font-heading font-bold text-primary-900 mb-2">
                    <AnimatedCounter end={500} suffix="M+" />
                  </p>
                  <p className="text-sm text-muted-foreground">{t('about_section.trade_volume')}</p>
                </div>
                <div>
                  <p className="text-4xl font-heading font-bold text-primary-900 mb-2">
                    <AnimatedCounter end={98} suffix="%" />
                  </p>
                  <p className="text-sm text-muted-foreground">{t('about_section.partners')}</p>
                </div>
              </div>
              <Link to="/about">
                <button className="group flex items-center gap-3 text-sm font-bold uppercase tracking-[0.15em] text-primary-900 hover:text-secondary-600 transition-colors">
                  {t('common.learn_more')}
                  <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </button>
              </Link>
            </motion.div>

            {/* Right Image Grid */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="aspect-[3/4] overflow-hidden">
                    <img 
                      src="https://images.pexels.com/photos/2933243/pexels-photo-2933243.jpeg?auto=compress&cs=tinysrgb&w=800" 
                      alt="Agricultural machinery"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="aspect-square bg-secondary-500 p-6 flex flex-col justify-end">
                    <Play className="w-10 h-10 text-primary-900 mb-4" />
                    <p className="text-sm font-bold text-primary-900">Watch Our Story</p>
                    <p className="text-xs text-primary-900/60">2 min video</p>
                  </div>
                </div>
                <div className="space-y-4 pt-8">
                  <div className="aspect-square overflow-hidden">
                    <img 
                      src="https://images.pexels.com/photos/5439439/pexels-photo-5439439.jpeg?auto=compress&cs=tinysrgb&w=800" 
                      alt="Handshake partnership"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="aspect-[3/4] overflow-hidden">
                    <img 
                      src="https://images.pexels.com/photos/2252584/pexels-photo-2252584.jpeg?auto=compress&cs=tinysrgb&w=800" 
                      alt="Grain harvest"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Categories Bento Grid */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16"
          >
            <div>
              <span className="inline-flex items-center gap-3 text-secondary-600 mb-4">
                <span className="w-12 h-px bg-secondary-500" />
                <span className="text-xs font-mono uppercase tracking-[0.3em]">Our Commodities</span>
              </span>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-primary-900 tracking-tight">
                {t('products.title')}
              </h2>
            </div>
            <Link to="/products">
              <button 
                className="group flex items-center gap-3 px-8 py-4 border-2 border-primary-900 text-primary-900 text-xs font-bold uppercase tracking-[0.15em] hover:bg-primary-900 hover:text-white transition-all duration-300"
                data-testid="view-all-products-btn"
              >
                {t('products.view_all')}
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </Link>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className={`bg-muted animate-pulse ${i === 0 ? 'md:col-span-2 md:row-span-2 min-h-[500px]' : 'min-h-[240px]'}`} />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {categories.slice(0, 6).map((category, index) => (
                <motion.div
                  key={category.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={index === 0 ? 'md:col-span-2 md:row-span-2' : ''}
                >
                  <CategoryCard category={category} size={index === 0 ? 'large' : 'normal'} />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Why Choose Us - Premium Grid */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a1628] to-[#060d17]" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: `radial-gradient(circle at 30% 20%, rgba(197, 160, 89, 0.2) 0%, transparent 50%),
                             radial-gradient(circle at 70% 80%, rgba(197, 160, 89, 0.15) 0%, transparent 40%)`
          }} />
        </div>
        
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mb-16"
          >
            <span className="inline-flex items-center gap-3 text-secondary-500 mb-4">
              <span className="w-12 h-px bg-secondary-500" />
              <span className="text-xs font-mono uppercase tracking-[0.3em]">{t('why_us.tagline')}</span>
            </span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white tracking-tight mb-6">
              {t('why_us.title')}
            </h2>
            <p className="text-lg text-white/50">
              {t('why_us.subtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyUsItems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group relative bg-white/5 backdrop-blur-sm border border-white/10 p-8 md:p-10 hover:border-secondary-500/30 hover:bg-white/[0.08] transition-all duration-500"
                data-testid={`why-us-${index}`}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-secondary-500/0 to-secondary-500/0 group-hover:from-secondary-500/5 group-hover:to-transparent transition-all duration-500" />
                
                <div className="relative">
                  <div className="w-14 h-14 bg-secondary-500/10 border border-secondary-500/30 flex items-center justify-center mb-6 group-hover:bg-secondary-500/20 group-hover:border-secondary-500/50 transition-all duration-500">
                    <item.icon className="w-6 h-6 text-secondary-500" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl font-heading font-bold text-white mb-3 group-hover:text-secondary-500 transition-colors duration-300">
                    {item.title}
                  </h3>
                  <p className="text-white/50 text-sm leading-relaxed group-hover:text-white/70 transition-colors duration-300">
                    {item.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      {!loading && products.length > 0 && (
        <section className="py-24 md:py-32 bg-[#f9f9f7]">
          <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-16"
            >
              <span className="inline-flex items-center gap-3 text-secondary-600 mb-4">
                <span className="w-12 h-px bg-secondary-500" />
                <span className="text-xs font-mono uppercase tracking-[0.3em]">Top Products</span>
              </span>
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-primary-900 tracking-tight">
                Featured Commodities
              </h2>
            </motion.div>

            <div className="space-y-6">
              {products.slice(0, 2).map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                >
                  <FeaturedProductCard product={product} />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section - Premium */}
      <section className="relative py-32 md:py-40 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?q=80&w=2070"
            alt="Global trade"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a1628]/98 via-[#0a1628]/95 to-[#0a1628]/80" />
        </div>
        
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-1/3 h-full opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 50% 50%, rgba(197, 160, 89, 0.3) 0%, transparent 60%)`
          }} />
        </div>
        
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-flex items-center gap-3 text-secondary-400 mb-8">
                <span className="w-12 h-px bg-secondary-500" />
                <span className="text-xs font-mono uppercase tracking-[0.3em]">{t('cta_section.tagline')}</span>
              </span>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white tracking-tight mb-8 leading-[1.1]">
                {t('cta_section.title')}
              </h2>
              <p className="text-lg text-white/60 mb-12 leading-relaxed max-w-xl">
                {t('hero.subtitle')}
              </p>
              <div className="flex flex-wrap gap-4">
                <MagneticButton>
                  <Link to="/rfq">
                    <button 
                      className="group relative overflow-hidden bg-secondary-500 px-10 py-5 text-sm font-bold uppercase tracking-[0.2em] text-primary-900"
                      data-testid="cta-rfq-btn"
                    >
                      <span className="relative z-10 flex items-center gap-3">
                        {t('nav.rfq')}
                        <ArrowUpRight className="w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </span>
                      <span className="absolute inset-0 bg-white translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
                    </button>
                  </Link>
                </MagneticButton>
                
                <MagneticButton>
                  <Link to="/contact">
                    <button 
                      className="group px-10 py-5 text-sm font-bold uppercase tracking-[0.2em] text-white border-2 border-white/30 hover:border-secondary-500 hover:text-secondary-500 transition-all duration-300"
                      data-testid="cta-contact-btn"
                    >
                      {t('nav.contact')}
                    </button>
                  </Link>
                </MagneticButton>
              </div>
            </motion.div>
            
            {/* Stats highlight */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="hidden lg:grid grid-cols-2 gap-6"
            >
              {[
                { number: '50+', label: t('stats.countries') },
                { number: '500+', label: t('stats.products') },
                { number: '1000+', label: t('stats.partners') },
                { number: '15+', label: t('stats.experience') },
              ].map((stat, index) => (
                <div 
                  key={index}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 text-center hover:border-secondary-500/30 transition-all duration-300"
                >
                  <div className="text-4xl md:text-5xl font-heading font-bold text-secondary-500 mb-2">
                    {stat.number}
                  </div>
                  <div className="text-sm text-white/50 uppercase tracking-wider">
                    {stat.label}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
